import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Select, Modal, Tooltip, Button, Input, AutoComplete } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Services from "../../services";
import Helpers from "../../services/helpers";
import Toaster from "../../services/toaster";
import useGlobal from "../../store";
import { useModalContext } from "../contexts/modalcontext";
import { useUserContext } from "../contexts/usercontext";
import {
  getLimitForMembershipTierAndCategory,
  isUserOfMembershipTier,
  MembershipTierLimitCategories,
  MembershipTiers,
  showMembershipTierLimitMessage,
} from "utils/user";

const SaveToFavoritesModal = () => {
  const router = useRouter();
  const {
    saveToFavoriteData: data,
    setSaveToFavoriteData,
    isSaveToFavoriteModalVisible: visible,
    setIsSaveToFavoriteModalVisible,
  } = useModalContext();
  const [globalState, globalActions] = useGlobal();

  const { user } = useUserContext();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      if (!selectedCategory) {
        return;
      }

      const { type, foreign_id } = data;

      const prevShallowFavorites = Array.from(globalState.shallowFavorites);

      if (isUserOfMembershipTier(user, MembershipTiers.STARTER)) {
        const favorites = prevShallowFavorites.filter((f) => f.type === type);
        const favoritesLimit = getLimitForMembershipTierAndCategory(
          user.membership_tier,
          MembershipTierLimitCategories.FAVORITES,
        );
        if (favorites.length >= favoritesLimit) {
          showMembershipTierLimitMessage(
            `You’ve reached your ${
              user.membership_tier
            } plan’s limit for ${type.toLowerCase()} favorites (${favoritesLimit}). Remove a ${type.toLowerCase()} favorite or upgrade your plan to add more ${type.toLowerCase()} favorites.`,
          );
          handleCancel();
          return;
        }
      }

      setLoading(true);

      const response = await Services.createFavorite({ type, foreign_id, category_id: selectedCategory });

      if (response.error) {
        return Toaster.ErrorNotification("Unable to save favorite.");
      }

      Toaster.SuccessNotification("Favorite saved!");
      const nextShallowFavorites = prevShallowFavorites.concat({
        foreign_id,
        type,
        id: response.id,
        title: response.title,
        category: response.category,
      });
      globalActions.setShallowFavorites(nextShallowFavorites);
      handleCancel();
    } catch (e) {
      Helpers.handleApiError(e, router);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSaveToFavoriteData();
    setIsSaveToFavoriteModalVisible(false);
  };

  const fetchCategories = async (type) => {
    try {
      setLoading(true);
      const res = await Services.getFavoriteCategories(type);

      if (res && res.error) {
        Toaster.ErrorNotification("Something went wrong, please try again!");
        return setIsSaveToFavoriteModalVisible(false);
      }

      if (Array.isArray(res) && res.length > 0) {
        setSelectedCategory(res[0].id);
      }

      setCategories(res);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data && data.type) {
      fetchCategories(data.type);
    }
  }, [data]);

  const resetAddingState = () => {
    setIsAdding(false);
    setNewCategory("");
  };

  const handleExistingCategory = (name) => {
    resetAddingState();
    const existingCategory = categories.find((cat) => cat.name === name) || {};
    setSelectedCategory(existingCategory.id || null);
  };

  const handleCategoryAdd = async () => {
    setAddLoading(true);
    const { type } = data;
    const body = {
      name: newCategory,
      type,
    };
    const result = await Services.createFavoriteCategory(body);
    if (result && !result.error) {
      await fetchCategories(type);
      resetAddingState();
      setSelectedCategory(result.id);
    } else {
      Toaster.ErrorNotification("We could not add a new category!");
    }
    setAddLoading(false);
  };

  return (
    <Modal
      visible={visible}
      title="Choose Favorite Category"
      onCancel={handleCancel}
      onOk={handleSubmit}
      okButtonProps={{ disabled: loading || !selectedCategory }}
    >
      {isAdding ? (
        <Input.Group style={{ width: "100%" }} compact>
          <AutoComplete
            placeholder={"e.g. Rising Players"}
            style={{ width: "calc(100% - 160px)" }}
            value={newCategory}
            onChange={setNewCategory}
            dataSource={categories.map((item) => item.name)}
            onSelect={handleExistingCategory}
            filterOption={Helpers.sciFilter}
          />
          <Button
            type={"primary"}
            style={{ width: 80 }}
            disabled={!newCategory}
            onClick={handleCategoryAdd}
            loading={addLoading}
          >
            Add
          </Button>
          <Button onClick={resetAddingState} style={{ width: 80 }}>
            Back
          </Button>
        </Input.Group>
      ) : (
        <div style={{ display: "flex" }}>
          <Select
            loading={loading}
            value={selectedCategory}
            onChange={(value) => setSelectedCategory(value)}
            style={{ width: "100%", marginRight: 10 }}
          >
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
          <Tooltip title={"Add another category"}>
            <Button type="link" icon={<PlusOutlined />} onClick={() => setIsAdding(true)} />
          </Tooltip>
        </div>
      )}
    </Modal>
  );
};

export default SaveToFavoritesModal;
